/**
 * Release versions are CalVer `YY.MM.PATCH` (e.g. `26.9.62`), written by CI
 * into `VITE_APP_VERSION` and into `/version.json` (#917). They must be
 * compared numerically: as strings, `26.9.100` sorts before `26.9.62` and
 * `26.10.0` before `26.9.1`.
 */
export type AppVersion = readonly [year: number, month: number, patch: number];

const VERSION_PATTERN = /^(\d{2})\.(\d{1,2})\.(\d{1,6})$/;

/** Parses `YY.MM.PATCH`; anything else (`dev`, `1.0`, `26.9.62-dev`, …) is null. */
export const parseAppVersion = (value: unknown): AppVersion | null => {
  if (typeof value !== "string") {
    return null;
  }
  const match = VERSION_PATTERN.exec(value.trim());
  if (!match) {
    return null;
  }
  return [Number(match[1]), Number(match[2]), Number(match[3])];
};

/** Negative when `a` is older than `b`, 0 when equal, positive when newer. */
export const compareAppVersions = (a: AppVersion, b: AppVersion): number =>
  a[0] - b[0] || a[1] - b[1] || a[2] - b[2];

/** True only when both parse and `candidate` is strictly newer than `running`. */
export const isNewerAppVersion = (candidate: unknown, running: unknown): boolean => {
  const next = parseAppVersion(candidate);
  const current = parseAppVersion(running);
  return next !== null && current !== null && compareAppVersions(next, current) > 0;
};

/** The canonical spelling, so `26.09.5` and `26.9.5` count as the same release. */
export const formatAppVersion = (version: AppVersion): string => version.join(".");

/**
 * The version this bundle was built as. Read at call time (not module load) so
 * tests can stub it; `dev` when the build had no version, which disables the
 * new-version check.
 */
export const runningAppVersion = (): string => import.meta.env.VITE_APP_VERSION || "dev";
